
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavigationItem {
  name: string;
  path: string;
}

interface MobileNavigationProps {
  mainNavItems: NavigationItem[];
  serviceNavItems: NavigationItem[];
  isOpen: boolean;
  onClose: () => void;
}

const MobileNavigation = ({ 
  mainNavItems, 
  serviceNavItems, 
  isOpen, 
  onClose 
}: MobileNavigationProps) => {
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const location = useLocation();
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isServiceActive = () => {
    const servicePaths = ["/offerings", "/hypnotherapy", "/trainings", "/somatic-coaching"];
    return servicePaths.includes(location.pathname);
  };

  const handleNavClick = (path: string) => {
    // If clicking on home link while already on home page, scroll to top
    if (path === "/" && location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    onClose();
  };

  // Custom trigger style that applies to all menu items for consistent hover effects
  const commonLinkStyle = "text-base font-bold uppercase tracking-wider text-gray-800 hover:text-kaija-accent transition-colors";
  
  if (!isOpen) return null;

  return (
    <nav className="md:hidden py-5 px-6 bg-gradient-to-b from-white to-kaija-pink/20">
      <div className="flex flex-col space-y-4">
        {mainNavItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              commonLinkStyle,
              isActive(item.path) ? "text-kaija-accent font-semibold" : ""
            )}
            onClick={() => handleNavClick(item.path)}
          >
            {item.name}
          </Link>
        ))}
        
        {/* Mobile Services Menu */}
        <div className="py-1">
          <button 
            className={cn(
              "flex items-center w-full justify-between",
              commonLinkStyle,
              isServiceActive() ? "text-kaija-accent font-semibold" : ""
            )}
            onClick={() => setServicesMenuOpen(!servicesMenuOpen)}
            aria-expanded={servicesMenuOpen}
          >
            <span>Teenused</span>
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${servicesMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          
          <div 
            className={`mt-2 space-y-2 overflow-hidden transition-all duration-300 ${
              servicesMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            {serviceNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "block pl-4 py-2.5 rounded-md transition-colors hover:bg-white/70",
                  "text-base font-medium text-gray-800 hover:text-kaija-accent",
                  isActive(item.path) ? "text-kaija-accent font-semibold bg-white/70" : ""
                )}
                onClick={() => handleNavClick(item.path)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
        
        {/* Kontakt as the last mobile menu item */}
        <Link
          to="/contact"
          className={cn(
            commonLinkStyle,
            isActive("/contact") ? "text-kaija-accent font-semibold" : ""
          )}
          onClick={() => handleNavClick("/contact")}
        >
          Kontakt
        </Link>
      </div>
    </nav>
  );
};

export default MobileNavigation;
